// src/CosmeticPage.js
import React, { useState } from 'react';
import { dedupeChecklist } from './utils/dedupeChecklist';

export default function CosmeticPage({
  checklist,
  images,
  paintThicknessHigh,
  paintThicknessLow
}) {
  const colorBlue = '#153BC6';
  const lightGray1 = '#f7f7f7';
  const lightGray2 = '#eaeaea';

  // State for image modal
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [zoomed, setZoomed] = useState(false);

  // Scroll to the Cosmetic section of the Damage Report
  function scrollToDamageReport() {
    const target =
      document.getElementById('damage-cosmetic') ||
      document.getElementById('damage-cosmetic-damage') ||
      document.getElementById('damage-report');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /* -------- render helpers (icon & images) --------------- */
  function renderDamageIcon(severity) {
    const baseStyle = {
      display: 'inline-block',
      verticalAlign: 'middle',
    };

    if (severity === 'Light') {
      // use the MagnifyingGlass.png from public/
      return (
        <img
          src="/MagnifyingGlass.png"
          alt="Inspect"
          style={{
            ...baseStyle,
            width: '1.4rem',
            height: '1.4rem',
          }}
        />
      );
    }

    if (severity === 'Moderate') {
      return <span style={{ ...baseStyle, color: 'orange', fontSize: '1.4rem' }}>&#9888;</span>;
    }
    if (severity === 'Severe') {
      return <span style={{ ...baseStyle, color: 'red', fontSize: '1.8rem' }}>&#10007;</span>;
    }
    return <span style={{ ...baseStyle, color: 'green', fontSize: '1.4rem' }}>&#10003;</span>;
  }

  // Keys for cosmetic images, in display order
  const cosmeticImageKeys = [
    'LeftFrontCornerImage',
    'FullLeftSideImage',
    'LeftRearCornerImage',
    'RearImage',
    'RoofImage',
    'RightRearCornerImage',
    'FullRightSideImage',
    'RightFrontCornerImage',
    'FrontImage'
  ];

  // Filter out any null URLs
  const cosmeticUrls = cosmeticImageKeys
    .map((key) => images[key])
    .filter(Boolean);

  // Modal handlers
  function openModal(idx) {
    setSelectedIndex(idx);
    setZoomed(false);
  }
  function closeModal() {
    setSelectedIndex(null);
    setZoomed(false);
  }
  function toggleZoom() {
    setZoomed((prev) => !prev);
  }
  function showPrev(e) {
    e.stopPropagation();
    if (selectedIndex > 0) {
      setSelectedIndex((i) => i - 1);
      setZoomed(false);
    }
  }
  function showNext(e) {
    e.stopPropagation();
    if (selectedIndex < cosmeticUrls.length - 1) {
      setSelectedIndex((i) => i + 1);
      setZoomed(false);
    }
  }

  // Deduplicate checklist entries, keep highest‑severity icon
  const uniqueChecklist = dedupeChecklist(checklist);

  return (
    <div
      className="report-page"
      style={{
        width: '8.5in',
        minHeight: '11in',
        margin: '0 auto',
        padding: '0.3in',
        backgroundColor: '#fff',
        border: '1px solid #ccc',
        boxSizing: 'border-box',
        fontFamily: 'Arial, sans-serif',
        position: 'relative'
      }}
    >
      <h2 style={{ marginBottom: '0.2in', color: colorBlue }}>
        Cosmetic Paint &amp; Panel Inspection
      </h2>

      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
        {/* Left column: Checklist + Paint Thickness */}
        <div style={{ flex: '0 0 50%' }}>
          {/* Checklist */}
          <div style={{ borderLeft: `5px solid ${colorBlue}`, paddingLeft: 8, marginBottom: '0.2in' }}>
            <h3 style={{ color: colorBlue, marginBottom: 4, fontSize: '1rem' }}>
              Paint &amp; Panel Checklist
            </h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {uniqueChecklist.map((item, idx) => {
                  const bgColor = idx % 2 === 0 ? lightGray1 : lightGray2;
                  const isClickable =
                    item.DamageSeverity === 'Light' ||
                    item.DamageSeverity === 'Moderate' ||
                    item.DamageSeverity === 'Severe';

                  return (
                    <tr key={item.DropdownValueId} style={{ backgroundColor: bgColor }}>
                      <td
                        style={{
                          padding: '4px 8px',
                          verticalAlign: 'middle'
                        }}
                      >
                        {item.DropdownValue}
                      </td>
                      <td
                        style={{
                          padding: '4px 8px',
                          textAlign: 'center',
                          verticalAlign: 'middle',
                          cursor: isClickable ? 'pointer' : 'default'
                        }}
                        onClick={isClickable ? scrollToDamageReport : undefined}
                        title={isClickable ? 'Jump to Damage Report' : undefined}
                      >
                        {renderDamageIcon(item.DamageSeverity)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Paint Thickness (moved under checklist) */}
          <div
            style={{
              borderLeft: `5px solid ${colorBlue}`,
              paddingLeft: 8,
              marginBottom: '0.2in'
            }}
          >
            <h3 style={{ color: colorBlue, marginBottom: 4, fontSize: '1rem' }}>
              Paint Thickness
            </h3>
            <p style={{ margin: '4px 8px' }}>
              <strong>High:</strong> {paintThicknessHigh ?? 'N/A'} mils
            </p>
            <p style={{ margin: '4px 8px' }}>
              <strong>Low:</strong> {paintThicknessLow ?? 'N/A'} mils
            </p>
          </div>
        </div>

        {/* Right column: Images & Notes */}
        <div style={{ flex: '0 0 50%' }}>
          {/* Cosmetic Images */}
          <div
            style={{
              borderLeft: `5px solid ${colorBlue}`,
              paddingLeft: 8,
              marginBottom: '0.2in'
            }}
          >
            <h3 style={{ color: colorBlue, marginBottom: 4, fontSize: '1rem' }}>
              Paint &amp; Panel Images
            </h3>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                rowGap: '4px',
                columnGap: '8px',
                alignItems: 'flex-start',
                alignContent: 'flex-start'
              }}
            >
              {cosmeticImageKeys.map((key) => {
                const url = images[key];
                if (!url) return null;
                const idx = cosmeticUrls.indexOf(url);
                return (
                  <div
                    key={key}
                    style={{
                      width: '80px',
                      height: '80px',
                      border: '1px solid #ccc',
                      borderRadius: 4,
                      overflow: 'hidden',
                      cursor: 'pointer'
                    }}
                    onClick={() => openModal(idx)}
                  >
                    <img
                      src={url}
                      alt={key}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Important Cosmetic Notes */}
          <div
            style={{
              borderLeft: `5px solid ${colorBlue}`,
              paddingLeft: 8,
              backgroundColor: '#f9f9f9',
              padding: '10px',
              marginBottom: '0.2in'
            }}
          >
            <h3 style={{ color: colorBlue, marginBottom: 6, fontSize: '1rem' }}>
              Important Cosmetic Notes
            </h3>
            <p style={{ margin: '6px 0' }}>
              Factory paint usually measures between 3.5 and 6.5 mils thick.
            </p>
            <p style={{ margin: '6px 0' }}>
              Low readings may mean polishing, sanding, or thinning over time.
            </p>
            <p style={{ margin: '6px 0' }}>
              High readings often point to repainted panels or body filler from past repairs.
            </p>
            <p style={{ margin: '6px 0' }}>
              Inconsistent readings can reveal work that might not show up on a Carfax—helping you spot hidden repairs.
            </p>
          </div>

          {/* Underside Rust Inspection */}
          <div
            style={{
              borderLeft: `5px solid ${colorBlue}`,
              paddingLeft: 8,
              backgroundColor: '#f9f9f9',
              padding: '10px'
            }}
          >
            <h3 style={{ color: colorBlue, marginBottom: 6, fontSize: '1rem' }}>
              Underside Rust Inspection
            </h3>
            <p style={{ margin: '6px 0' }}>
              We check the vehicle’s underside for rust and rate it in two categories:
            </p>
            <p style={{ margin: '4px 20px' }}>
              <strong>Light Rust</strong> – Thin surface rust on exposed metal; common and usually cosmetic
            </p>
            <p style={{ margin: '4px 20px' }}>
              <strong>Heavy Rust</strong> – Flaky, layered, or crusty rust; may point to long-term exposure or poor care
            </p>
            <p style={{ margin: '6px 0' }}>
              This is not the same as structural rust, but it does give insight into the overall condition of the underbody. Heavy rust may lead to future issues and is worth a closer look before purchase.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom-right logo */}
      <div style={{ position: 'absolute', bottom: 10, right: 10 }}>
        <img src="/logo.png" alt="Company Logo" style={{ height: 40 }} />
      </div>

      {/* Image Modal Overlay */}
      {selectedIndex !== null && cosmeticUrls[selectedIndex] && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={closeModal}
        >
          <img
            src={cosmeticUrls[selectedIndex]}
            alt="Enlarged"
            onClick={(e) => {
              e.stopPropagation();
              toggleZoom();
            }}
            style={{
              maxWidth: zoomed ? '95%' : '50%',
              maxHeight: zoomed ? '95%' : '60%',
              border: '2px solid #fff',
              borderRadius: 6,
              cursor: zoomed ? 'zoom-out' : 'zoom-in',
              transition: 'all 0.3s ease'
            }}
          />

          {selectedIndex > 0 && (
            <div
              onClick={showPrev}
              style={{
                position: 'absolute',
                left: 20,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                fontSize: '3rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              &#10094;
            </div>
          )}
          {selectedIndex < cosmeticUrls.length - 1 && (
            <div
              onClick={showNext}
              style={{
                position: 'absolute',
                right: 20,
                top: '50%',
                transform: 'translateY(-50%)',                color: 'white',
                fontSize: '3rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              &#10095;
            </div>
          )}
          <div
            onClick={(e) => {
              e.stopPropagation();
              closeModal();
            }}
            style={{
              position: 'absolute',
              top: 10,
              right: 20,
              color: 'white',
              fontSize: '2rem',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            &times;
          </div>
        </div>
      )}
    </div>
  );
}
